/**
 * @ab/game-core: the deterministic simulation (ADR 0001).
 *
 * Everything the game does to a run goes through `apply(ctx, state, command)`.
 * `createGame(content)` binds the content pack once and returns the bound
 * `apply` and `createRun`. This file is the only public surface of the package.
 * The wiki page Architecture → game-core contract is the shape.
 */
import type { ContentPack } from '@ab/content-schema';
import { type Command, apply } from './commands/index.ts';
import { itemUsability } from './commands/use_item.ts';
import { previewTargets, skillUsability } from './combat.ts';
import type { Dir } from './grid.ts';
import type { ApplyResult } from './commands/types.ts';
import { type BagEntry, type PartyMember, type RunState, createRun } from './state.ts';

/** Bumped whenever a change would make an old command log replay differently (ADR 0002). */
export const coreVersion = '0.7.0';

export type Game = {
  readonly content: ContentPack;
  /** `createRun` with this content: the party, and the stacks the bag starts with (empty when left out). */
  readonly createRun: (dungeon: string, party: readonly PartyMember[], bag?: readonly BagEntry[]) => RunState;
  readonly apply: (state: RunState, command: Command) => ApplyResult;
  /** `skillUsability` with this content: the `InvalidCommand` message if the skill could not be used now ignoring targeting, else null. */
  readonly skillUsability: (state: RunState, userId: string, skillId: string) => string | null;
  /** `itemUsability` with this content: the `InvalidCommand` message if the item could not be used now ignoring targeting, else null. */
  readonly itemUsability: (state: RunState, itemId: string) => string | null;
  /** `previewTargets` with this content: the unit ids a skill or item without a `single` target would reach facing `dir`, or null. */
  readonly previewTargets: (state: RunState, userId: string, id: string, dir: Dir) => string[] | null;
};

export const createGame = (content: ContentPack): Game => ({
  content,
  createRun: (dungeon, party, bag = []) => createRun(content, dungeon, party, bag),
  apply: (state, command) => apply({ content }, state, command),
  skillUsability: (state, userId, skillId) => skillUsability(content, state, userId, skillId),
  itemUsability: (state, itemId) => itemUsability(content, state, itemId),
  previewTargets: (state, userId, id, dir) => previewTargets(content, state, userId, id, dir),
});

export { apply, type Command } from './commands/index.ts';
export type { ApplyResult, CommandHandler, Ctx } from './commands/types.ts';
export type { DamageCause, Event, HealCause, SkipReason } from './events.ts';
export { type Dir, type GridView, type Pos, type Walk, type WalkTable, canStep, dirs, dirOffset, isDir, manhattan, chebyshev, stepIn, walkIn, walkTable } from './grid.ts';
export { gridView, reachableFrom } from './floorview.ts';
export { basicHitChance, defeatLoss, hitChance, previewTargets, singleTargets, skillUsability, speedAccuracyBonus } from './combat.ts';
export { itemUsability } from './commands/use_item.ts';
export { type RngState, seedRng, nextInt, roll } from './rng.ts';
export {
  type BagEntry,
  type BossScaling,
  type Equipment,
  type Floor,
  type FloorItem,
  type FloorSpring,
  type HumanMember,
  type MonsterMember,
  type PartyMember,
  type Phase,
  type RefusalArgs,
  type RefusalCode,
  type Room,
  type RunState,
  type Side,
  type StageStat,
  type Status,
  type Streams,
  type Tile,
  type Unit,
  type UnitKind,
  InvalidCommand,
  compareUnitIds,
  refusalCodes,
  createRun,
  leaderOf,
  seedStreams,
  unitById,
} from './state.ts';
export { type StatName, type UnitLike, unitStat, typesOf, stageMultiplier, stageTotal, expToNext } from './stats.ts';
export { canonicalJson } from './determinism.ts';
